import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import toast, { Toaster } from 'react-hot-toast'
import { BrowserRouter, Route, Routes } from 'react-router'

import {
  BrowserCheck,
  SliderStackProvider,
  TooltipProvider,
} from '@fv/client-components'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  redirectToLogin,
  showDevtools,
} from '@fv/client-core'

import { AuthenticatedApp } from './components/AuthenticatedApp'
import ErrorBoundary from './components/layout/ErrorBoundary'
import { marketingUri } from './constants'
import Login from './features/auth/Login'
import { SharedShipmentPage } from './features/share/SharedShipmentPage'

const loginRedirect = `${marketingUri}/login`
const staleTime = 60 * 1000 * 2 // 2 minutes

function handleApiError(error: unknown) {
  const status = (error as { status?: number })?.status

  if (status === 401) {
    toast.error('Access denied, redirecting to login.', { id: 'unauthorized' })
    redirectToLogin()
  }

  if (
    status === 402 &&
    !window.location.pathname.startsWith('/settings/subscription')
  ) {
    toast.error('Payment required, redirecting to subscription.', {
      id: 'payment-required',
    })
    window.location.href = `/settings/subscription`
  }

  if (status === 403) {
    toast.error(
      'You do not have access to the requested data. Please contact customer support.',
      { id: 'forbidden' },
    )
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  defaultOptions: {
    mutations: { onError: handleApiError },
    queries: {
      staleTime,
      retry: (failCount, error) => {
        const status = (error as { status?: number })?.status
        if (
          status === 401 ||
          status === 403 ||
          status === 402 ||
          status === 504
        )
          return false
        return failCount < 3
      },
    },
  },
})

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delay={300} closeDelay={100}>
        <BrowserCheck>
          <BrowserRouter>
            <ErrorBoundary>
              <SliderStackProvider>
                <Routes>
                  <Route
                    path="share/:loadId"
                    element={<SharedShipmentPage />}
                  />

                  <Route
                    path="login/:token"
                    element={<Login loginRedirect={loginRedirect} />}
                  />

                  <Route path="*" element={<AuthenticatedApp />} />
                </Routes>
              </SliderStackProvider>
            </ErrorBoundary>
          </BrowserRouter>

          {showDevtools && (
            <ReactQueryDevtools
              client={queryClient}
              initialIsOpen={false}
              buttonPosition="bottom-left"
            />
          )}

          <Toaster
            position="bottom-center"
            toastOptions={{
              style: {
                border: '2px solid #dddddd',
              },
            }}
          />
        </BrowserCheck>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

export default App
